import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Mail, Lock, ArrowRight, Store, ShieldCheck, BarChart3, Package,
    AlertCircle, Loader2, Eye, EyeOff, Headphones, LockKeyhole, Monitor,
} from 'lucide-react';
import { isCajeroUser } from '../utils/userRoles';

const REMEMBER_KEY = 'erp_login_email';

export default function LoginPage() {
    const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(() => Boolean(localStorage.getItem(REMEMBER_KEY)));
    const [showPassword, setShowPassword] = useState(false);
    const [forgotHint, setForgotHint] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const res = await login(email, password);

        if (res.success) {
            if (remember) localStorage.setItem(REMEMBER_KEY, email);
            else localStorage.removeItem(REMEMBER_KEY);
            setIsLoading(false);
            if (isCajeroUser(res.user)) {
                navigate(res.requiresOpenCaja ? '/pos?openCaja=1' : '/pos');
            } else {
                navigate('/');
            }
        } else {
            setError(res.error || 'Credenciales incorrectas');
            setIsLoading(false);
        }
    };

    return (
        <div className="login-screen">
            <div className="login-bg" role="img" aria-label="Local del minimarket" />
            <div className="login-shade" />

            <div className="login-layout">
                <section className="login-hero">
                    <div className="login-brand">
                        <span className="login-brand-mark" aria-hidden>
                            <Store size={22} strokeWidth={2.25} />
                        </span>
                        <span>ERP Minimarket</span>
                    </div>

                    <h1>
                        Todo tu negocio
                        <span>en un solo lugar</span>
                    </h1>
                    <p className="login-lead">
                        Controla tu inventario, ventas y proveedores de forma simple y eficiente.
                    </p>

                    <div className="login-features">
                        <article>
                            <Package size={22} aria-hidden />
                            <strong>Inventario</strong>
                            <span>Control total de productos</span>
                        </article>
                        <article>
                            <BarChart3 size={22} aria-hidden />
                            <strong>Reportes</strong>
                            <span>Decisiones más rápidas</span>
                        </article>
                        <article>
                            <ShieldCheck size={22} aria-hidden />
                            <strong>Seguro</strong>
                            <span>Datos siempre protegidos</span>
                        </article>
                    </div>

                    <p className="login-tagline">Hacemos crecer tu negocio</p>
                </section>

                <section className="login-card">
                    <div className="login-card-logo" aria-hidden>
                        <Store size={26} strokeWidth={2.25} />
                    </div>
                    <p className="login-card-brand">ERP Minimarket</p>
                    <h2>Bienvenido</h2>
                    <p className="login-card-sub">
                        Ingresa tus credenciales para acceder al sistema
                    </p>

                    {error && (
                        <div className="login-error" role="alert">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <label className="login-field">
                            <span className="login-field-label">
                                <Mail size={15} aria-hidden /> Correo Electrónico
                            </span>
                            <span className="login-input">
                                <Mail size={16} aria-hidden />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@example.com"
                                    autoComplete="username"
                                    required
                                />
                            </span>
                        </label>

                        <label className="login-field">
                            <span className="login-field-label">
                                <Lock size={15} aria-hidden /> Contraseña
                            </span>
                            <span className="login-input">
                                <LockKeyhole size={16} aria-hidden />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="login-eye"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </span>
                        </label>

                        <div className="login-row">
                            <label className="login-remember">
                                <input
                                    type="checkbox"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                />
                                Recordar mi sesión
                            </label>
                            <button
                                type="button"
                                className="login-forgot"
                                onClick={() => setForgotHint(true)}
                            >
                                ¿Olvidaste tu contraseña?
                            </button>
                        </div>

                        {forgotHint && (
                            <p className="login-forgot-hint">
                                Pide el cambio al administrador del sistema.
                            </p>
                        )}

                        <button type="submit" className="login-submit" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 size={18} className="spinning" />
                                    Ingresando...
                                </>
                            ) : (
                                <>
                                    <ArrowRight size={18} />
                                    Ingresar al Sistema
                                </>
                            )}
                        </button>
                    </form>

                    <p className="login-help" id="login-help">
                        ¿Problemas para acceder? Contacta al administrador
                    </p>
                </section>
            </div>

            <ul className="login-trust">
                <li>
                    <Headphones size={18} aria-hidden />
                    <span><strong>Soporte</strong>Siempre disponible</span>
                </li>
                <li>
                    <Lock size={18} aria-hidden />
                    <span><strong>Información segura</strong>Datos protegidos</span>
                </li>
                <li>
                    <Monitor size={18} aria-hidden />
                    <span><strong>Sistema en la nube</strong>Accede desde cualquier lugar</span>
                </li>
            </ul>
        </div>
    );
}
